import type { StarfishIconRef } from '@/app/common/icons/starfish';
import type { TranslationFunction } from '@/app/components/translations/Translation';
import type { HotelStaticFacilityGroup } from '@/app/components/hotel-detail/common/staticHotelDataAdapter';
import { FACILITY_ICONS } from '@/app/components/hotel-detail/hooks/useExtendedSortedHotelServices';

export type EvoFacilityCategory = {
  key: string;
  title: string;
  icon: StarfishIconRef;
  items: string[];
  // Only the long catch-all category may flow across the two columns.
  breakColumn: boolean;
};

// One glyph per results facility group; the groups the old categories already covered keep theirs.
const FACILITY_GROUP_ICONS: Partial<Record<string, StarfishIconRef>> = {
  accessibility: FACILITY_ICONS.accessibility,
  activities: FACILITY_ICONS.sports,
  food_drink: FACILITY_ICONS.drink,
  general: FACILITY_ICONS.info,
  internet: FACILITY_ICONS.wifi,
  kitchen: { category: 'Services', name: 'icon-oven', color: 'iconPrimary' },
  outdoors: { category: 'Services', name: 'icon-outdoor-area', color: 'iconPrimary' },
  parking: FACILITY_ICONS.parking,
  pool_wellness: FACILITY_ICONS.pool,
  safety_security: { category: 'Security and safety', name: 'icon-shield', color: 'iconPrimary' },
  services: FACILITY_ICONS.reception,
  ski: { category: 'Services', name: 'icon-ski', color: 'iconPrimary' },
  transport: { category: 'Transportation', name: 'icon-bus', color: 'iconPrimary' },
};

/**
 * The normalized facility groups as the EVO facilities section lists them (MPH-237): sorted by the title the
 * user reads, in the page language, and without the groups whose title is not translated in that language —
 * a list of facilities with no heading above it is not shown.
 */
export function buildFacilityCategories(
  groups: HotelStaticFacilityGroup[] | undefined,
  t: TranslationFunction,
  lang: string | undefined
): EvoFacilityCategory[] {
  const collator = new Intl.Collator(lang);

  return (groups ?? [])
    .map(({ id, titleKey, facilities }) => ({
      key: id,
      title: t(titleKey),
      icon: FACILITY_GROUP_ICONS[id] ?? FACILITY_ICONS.info,
      items: facilities,
      breakColumn: id === 'general',
    }))
    .filter((category) => category.title.trim())
    .sort((a, b) => collator.compare(a.title, b.title));
}
